<template>
  <div class="table-sticky">
    <v-client-table :data="tableData" :columns="tableColumns" :options="tableOptions">
      <template #menu="{ row }">
        <span class="text-nowrap">{{ menuLabel(row.menu) }}</span>
      </template>
      <template #menuPermission="{ row }">
        <input type="checkbox" :checked="hasMenuPermission(row.menu)" @click="handleSelectMenuPermission(row.menu)" />
      </template>
      <template #readPermission="{ row }">
        <div class="align-left">
          <p class="read column">
            <input
              type="checkbox"
              :checked="hasPermission(row.menu, 'Read', '') || hasPermission(row.menu, 'Write', '')"
              v-indeterminate="hasPartialColumns(row.menu)"
              @click="handleSelectPermission(row.menu, 'Read', '')"
            />
            {{ $t("users.read") }}
          </p>
          <p class="column" :key="col" v-for="col in getTableColumns(row.menu)">
            <input type="checkbox" :checked="hasPermission(row.menu, 'LimitRead', col) || hasPermission(row.menu, 'LimitRead', 'ALL')" @click="handleSelectPermission(row.menu, 'LimitRead', col)" />
            {{ readLabel(col) }}
          </p>
        </div>
      </template>
      <template #writePermission="{ row }"
        ><div class="align-left" v-if="row.writePermission">
          <p class="write column">
            <input
              type="checkbox"
              :checked="hasPermission(row.menu, 'Write', '')"
              v-indeterminate="hasPartialWriteColumns(row.menu)"
              @click="handleSelectPermission(row.menu, 'Write', '')"
            />
            {{ $t("users.write") }}
          </p>
          <p class="column" :key="col" v-for="col in getTableWriteColumns(row.menu)">
            <input type="checkbox" :checked="hasWritePermission(row.menu, 'Write', col)" @click="handleSelectWritePermission(row.menu, 'Write', col)" />
            {{ writeLabel(col) }}
          </p>
        </div>
      </template>
    </v-client-table>
  </div>
</template>

<script>
import { cloneDeep, remove } from "lodash";
import axiosFactory from "../../services/axios.factory";

export default {
  components: {},
  props: {
    userData: null,
  },
  data() {
    return {
      user: cloneDeep(this.userData),
      tableColumnPermissions: [],
      tableWriteColumns: {},
      tableColumns: ["menu", "menuPermission", "readPermission", "writePermission"],
      // Reihenfolge folgt dem Menue (siehe UserService.GetTableColumns).
      tableData: [
        { menu: "Dashboard", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "ProcurementDashboard", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "Procurement", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "ShippingPlans", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "Products", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "Offers", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "GeneralCosts", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "OqsSettings", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "Orders", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "RulesSqp", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "StreetDirectory", menuPermission: true, readPermission: [], writePermission: false },
        { menu: "Suppliers", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "ParcelCalculator", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "Returns", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "ReturnPhotoTypes", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "WmsDashboard", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "Inventories", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "Warehouses", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "StorageLocations", menuPermission: true, readPermission: [], writePermission: true },
        { menu: "InventoryManagementExport", menuPermission: true, readPermission: [], writePermission: false },
        { menu: "WarehousesHistory", menuPermission: true, readPermission: [], writePermission: false },
        { menu: "PhotoManager", menuPermission: true, readPermission: [], writePermission: false },
      ],
      tableOptions: {
        headings: {
          menu: () => this.$t("users.menu"),
          menuPermission: () => this.$t("users.menuPermission"),
          readPermission: () => this.$t("users.readPermission"),
          writePermission: () => this.$t("users.writePermission"),
        },
        columnsClasses: {
          menu: "text-left",
          menuPermission: "text-left",
          readPermission: "text-left",
          writePermission: "text-left",
        },
        perPage: 20,
        perPageValues: [20],
        filterable: false,
        sortable: [],
        params: {},
        resizableColumns: false,
        pagination: {
          show: false,
        },
      },
    };
  },
  directives: {
    // In Vue 2.x
    indeterminate: function (el, binding) {
      el.indeterminate = Boolean(binding.value);
    },
  },
  computed: {
    roleOptions() {
      return this.$store.state.users.roles || [];
    },
    permissionOptions() {
      return ["LimitRead", "Read", "Write"];
    },
  },
  methods: {
    menuLabel(menu) {
      const key = `users.menuNames.${menu}`;
      return this.$te(key) ? this.$t(key) : menu;
    },
    writeLabel(col) {
      const key = `users.writeNames.${col}`;
      return this.$te(key) ? this.$t(key) : col;
    },
    // Ohne Uebersetzung bleibt der technische Spaltenname stehen, wie bisher.
    readLabel(col) {
      const key = `users.readNames.${col}`;
      return this.$te(key) ? this.$t(key) : col;
    },
    getTableColumns(menu) {
      if (this.tableColumnPermissions && this.tableColumnPermissions.hasOwnProperty(menu)) {
        return this.tableColumnPermissions[menu];
      }

      return [];
    },
    getTableWriteColumns(menu) {
      if (this.tableWriteColumns && this.tableWriteColumns.hasOwnProperty(menu)) {
        return this.tableWriteColumns[menu];
      }

      return [];
    },
    hasMenuPermission(menu) {
      return this.user && this.user.menuPermissions && this.user.menuPermissions.includes(menu);
    },
    handleSelectMenuPermission(menu) {
      if (this.user.menuPermissions.includes(menu)) {
        remove(this.user.menuPermissions, (p) => p === menu);
        // Ohne Menuepunkt ergeben Rechte keinen Sinn -> Lesen/Schreiben/Spalten komplett entfernen
        if (this.user.tableColumnPermissions[menu]) {
          this.user.tableColumnPermissions[menu] = { Read: [], Write: [], LimitRead: [] };
        }
      } else {
        this.user.menuPermissions.push(menu);
      }

      this.user.menuPermissions = cloneDeep(this.user.menuPermissions);
      this.user.tableColumnPermissions = cloneDeep(this.user.tableColumnPermissions);

      this.$emit("userPerimissionsUpdated", {
        tableColumnPermissions: this.user.tableColumnPermissions,
        menuPermissions: this.user.menuPermissions,
      });
    },
    hasPermission(table, permission, col) {
      if (!this.user) return false;

      if (!this.user.tableColumnPermissions[table]) return false;

      if (!this.user.tableColumnPermissions[table][permission]) return false;

      if (permission === "Read" || permission === "Write") {
        return this.user.tableColumnPermissions[table][permission].includes("ALL");
      }
      if (permission === "LimitRead" && col === "") {
        return this.user.tableColumnPermissions[table][permission].length > 0;
      }

      return this.user.tableColumnPermissions[table][permission].includes(col);
    },
    hasWritePermission(table, permission, col) {
      if (!this.user) return false;

      if (!this.user.tableColumnPermissions[table]) return false;

      if (!this.user.tableColumnPermissions[table][permission]) return false;

      return this.user.tableColumnPermissions[table][permission].includes(col);
    },
    hasPartialColumns(table) {
      const perms = this.user && this.user.tableColumnPermissions && this.user.tableColumnPermissions[table];
      if (!perms || !perms.LimitRead) return false;
      const cols = perms.LimitRead.filter((c) => c !== "ALL");
      const total = this.getTableColumns(table).length;
      const fullRead = (perms.Read && perms.Read.includes("ALL")) || (perms.Write && perms.Write.includes("ALL"));
      return cols.length > 0 && cols.length < total && !fullRead;
    },
    // Teilauswahl bei den Write-Unterpunkten: Strich statt Haken (analog zu Lesen)
    hasPartialWriteColumns(table) {
      const perms = this.user && this.user.tableColumnPermissions && this.user.tableColumnPermissions[table];
      if (!perms || !perms.Write) return false;
      const cols = perms.Write.filter((c) => c !== "ALL");
      return cols.length > 0 && !perms.Write.includes("ALL");
    },
    // Invarianten des Rechtemodells: Schreiben => Lesen; irgendein Recht => Menuepunkt sichtbar; kein Recht => Menuepunkt weg
    normalizePermissions(table) {
      const perms = this.user.tableColumnPermissions[table] || {};
      const allColumns = this.getTableColumns(table).filter((c) => c !== "ALL");

      if (perms.Write && perms.Write.includes("ALL")) {
        perms.Read = ["ALL"];
        perms.LimitRead = [...allColumns];
      }

      const hasAnyRight = (perms.Write && perms.Write.length > 0) || (perms.Read && perms.Read.includes("ALL")) || (perms.LimitRead && perms.LimitRead.length > 0);

      if (hasAnyRight && !this.user.menuPermissions.includes(table)) {
        this.user.menuPermissions.push(table);
      }
      if (!hasAnyRight) {
        remove(this.user.menuPermissions, (p) => p === table);
      }

      this.user.tableColumnPermissions[table] = perms;
    },
    handleSelectPermission(table, permission, col) {
      if (!this.user.tableColumnPermissions[table]) {
        this.user.tableColumnPermissions[table] = {};
      }
      const perms = this.user.tableColumnPermissions[table];
      const allColumns = this.getTableColumns(table).filter((c) => c !== "ALL");

      if (permission === "Write") {
        // Verhalten wie beim Lesen: Anwaehlen setzt Marker + ALLE Unterpunkte,
        // Abwaehlen leert alles (Lesen und Lese-Spalten bleiben erhalten)
        if (perms.Write && perms.Write.includes("ALL")) {
          perms.Write = [];
        } else {
          perms.Write = ["ALL", ...this.getTableWriteColumns(table)];
        }
      } else if (permission === "Read") {
        if ((perms.Read && perms.Read.includes("ALL")) || (perms.Write && perms.Write.includes("ALL"))) {
          // Lesen abwaehlen: Schreiben kann ohne Lesen nicht bestehen, Spalten haengen am Lesen
          perms.Read = [];
          perms.Write = [];
          perms.LimitRead = [];
        } else {
          perms.Read = ["ALL"];
          perms.LimitRead = [...allColumns];
        }
      } else {
        // Einzelspalte (LimitRead)
        if (!perms.LimitRead) perms.LimitRead = [];
        if (perms.LimitRead.includes(col)) {
          remove(perms.LimitRead, (p) => p === col);
          // Teilauswahl: volles Lesen/Schreiben gilt nicht mehr — explizite Write-Unterpunkte bleiben
          perms.Read = [];
          perms.Write = (perms.Write || []).filter((c) => c !== "ALL");
        } else {
          perms.LimitRead.push(col);
          if (perms.LimitRead.filter((c) => c !== "ALL").length === allColumns.length) {
            perms.Read = ["ALL"];
          }
        }
      }

      this.normalizePermissions(table);

      this.user.tableColumnPermissions = cloneDeep(this.user.tableColumnPermissions);
      this.user.menuPermissions = cloneDeep(this.user.menuPermissions);

      this.$emit("userPerimissionsUpdated", {
        tableColumnPermissions: this.user.tableColumnPermissions,
        menuPermissions: this.user.menuPermissions,
      });
    },
    handleSelectWritePermission(table, permission, col) {
      if (!this.user.tableColumnPermissions[table]) {
        this.user.tableColumnPermissions[table] = {};
      }
      const perms = this.user.tableColumnPermissions[table];
      const writeCols = this.getTableWriteColumns(table);
      if (!perms[permission]) {
        perms[permission] = [];
      }
      if (perms[permission].includes(col)) {
        // Unterpunkt abwaehlen: volles Schreiben gilt nicht mehr -> Marker weg, Haupt-Checkbox zeigt Strich
        perms[permission] = perms[permission].filter((i) => i !== col && i !== "ALL");
      } else {
        perms[permission].push(col);
        // alle Unterpunkte gewaehlt -> volles Schreiben (analog zu Lesen mit allen Spalten)
        if (writeCols.every((c) => perms[permission].includes(c)) && !perms[permission].includes("ALL")) {
          perms[permission].push("ALL");
        }
      }

      this.normalizePermissions(table);

      this.user.tableColumnPermissions = cloneDeep(this.user.tableColumnPermissions);
      this.user.menuPermissions = cloneDeep(this.user.menuPermissions);

      this.$emit("userPerimissionsUpdated", {
        tableColumnPermissions: this.user.tableColumnPermissions,
        menuPermissions: this.user.menuPermissions,
      });
    },
    updateUserPermissions(tableColumnPermissions) {
      // Aktuellen Bearbeitungsstand als Basis nehmen, nicht das Original (this.userData).
      // Sonst ueberschreibt der spaet eintreffende table_columns-Fetch Klicks, die davor passiert sind.
      const u = cloneDeep(this.user);

      const userPermissions = Object.entries(u.tableColumnPermissions).reduce((acc, [table, permissions]) => {
        if (permissions?.Write?.includes("ALL")) {
          permissions.Read = ["ALL"];
        }
        let LimitRead = permissions?.LimitRead;
        const shouldMap = (permissions?.LimitRead?.includes("ALL") || permissions?.Read?.includes("ALL")) && !permissions?.LimitRead?.length;
        if (shouldMap) {
          const allColumns = this.getTableColumns(table);
          LimitRead = allColumns.map((col) => col);
        }
        acc[table] = {
          Read: permissions?.Read,
          Write: permissions?.Write,
          LimitRead: LimitRead,
        };

        return acc;
      }, {});

      this.user.tableColumnPermissions = userPermissions;
    },
  },
  created() {
    Promise.all([this.$store.dispatch("users/getUsers"), this.$store.dispatch("users/getRoles")]).finally((_) => {
      this.isLoading = false;
    });
    axiosFactory()
      .get(`/users/table_write_columns`)
      .then((res) => {
        this.tableWriteColumns = res.data.data;
      });
    axiosFactory()
      .get(`/users/table_columns`)
      .then((res) => {
        this.tableColumnPermissions = res.data.data;
        this.updateUserPermissions(res.data.data);
      });
  },
};
</script>
<style scoped>
p.column {
  margin-bottom: 5px;
}
p.read,
p.write {
  margin-bottom: 20px;
}
.align-left {
  text-align: left;
}
</style>
